import { fieldFindingRespondActionSchema } from '@mio/shared';
import { useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import { decisionHistoryGroups, decisionOwnerConversation } from '../field-decision-conversation';
import { describeFieldSource } from '../field-object-labels';

import { FieldChatMarkdown } from './field-chat-markdown';
import { FieldDecisionForm } from './field-decision-form';
import { FieldEvidencePicker } from './field-discovery-inputs';
import { FieldEmpty, FieldPanelHeading, FieldStatus, FieldTime } from './field-primitives';
import { FieldTicketForm } from './field-ticket-form';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import './field-objects.css';
import './field-discovery.css';

import type { FieldPanelProps } from './field-types';
import type { FieldDestination } from '../field-overview';
import type { FieldActorId, FieldDecision, FieldFinding, FieldSnapshot } from '@mio/shared';

const stakeholderQuestions: Record<FieldActorId, string> = {
  jordan: 'Which advisor journey should work first, and what should the advisor see at each step?',
  marcus: 'Who is the pilot for, how will we measure success, and what does margin mean here?',
  elena:
    'Who must approve a proposal, when does that approval expire, and what must never bypass it?',
  noor: 'Which system owns the order, and which existing contracts and integrations should I preserve?',
  amara: 'What should an operator see when something fails, and what does safe recovery look like?',
};

const categories: Record<FieldFinding['category'], { label: string; meaning: string }> = {
  defect: { label: 'Defect', meaning: 'Observed behavior differs from the agreed requirement.' },
  missed_discovery: {
    label: 'Discovery gap',
    meaning: 'A relevant constraint may have been missed. Review what was available at the time.',
  },
  clarification: {
    label: 'Clarification',
    meaning: 'The expected behavior needs a clearer shared definition.',
  },
  scope_change: {
    label: 'Scope change',
    meaning: 'New or changed expectations need an explicit scope decision.',
  },
  improvement: {
    label: 'Improvement',
    meaning: 'A possible enhancement to weigh against the agreed scope.',
  },
  prevention: {
    label: 'Prevention',
    meaning: 'Evidence shows the implementation handled a potential failure.',
  },
};

function EvidenceList({
  snapshot,
  ids,
  onNavigate,
  revisions,
}: {
  snapshot: FieldSnapshot;
  ids: string[];
  onNavigate: (destination: FieldDestination) => void;
  revisions?: Record<string, string>;
}) {
  return ids.length ? (
    <ul className="field-discovery-evidence">
      {ids.map((id) => {
        const message = snapshot.messages.find((item) => item.id === id);
        const destination: FieldDestination | undefined = snapshot.discovery?.decisions.some(
          (item) => item.id === id,
        )
          ? { section: 'engagement', focusId: id }
          : snapshot.documents.some((item) => item.id === id)
            ? { section: 'documents', focusId: id }
            : snapshot.prs.some((item) => item.id === id)
              ? { section: 'reviews', focusId: id }
              : snapshot.runs.some((item) => item.id === id)
                ? { section: 'operations', focusId: id }
                : message
                  ? {
                      section: 'inbox',
                      actorId: message.actorId,
                      focusId: message.id,
                      ...(message.meetingId ? { meetingId: message.meetingId } : {}),
                    }
                  : undefined;
        return (
          <li key={id}>
            {destination ? (
              <button type="button" onClick={() => onNavigate(destination)}>
                {describeFieldSource(snapshot, id)}
              </button>
            ) : (
              <span>{describeFieldSource(snapshot, id)}</span>
            )}
            {revisions?.[id] ? (
              <span className="ml-2" title={revisions[id]}>
                Recorded revision {revisions[id]?.slice(0, 12)}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  ) : null;
}

function DecisionConversation({
  snapshot,
  decision,
  busy,
  onAction,
  onNavigate,
}: FieldPanelProps & {
  decision: FieldDecision;
  onNavigate: (destination: FieldDestination) => void;
}) {
  const { request, operation, reply, linked } = decisionOwnerConversation(snapshot, decision);
  const owner =
    snapshot.actors.find((actor) => actor.id === decision.owner)?.name.split(' ')[0] ?? 'The owner';
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState('');
  const sending = useRef(false);
  const pending = operation?.status === 'pending' || operation?.status === 'running';
  const failed = operation?.status === 'failed';
  const canRetry = linked && failed;
  const target = reply ?? request;
  const recordMeaning =
    decision.status === 'confirmed'
      ? `The owner confirmed version ${decision.version}.`
      : decision.status === 'deferred'
        ? 'The owner deferred this record. It has not been confirmed.'
        : decision.kind === 'question'
          ? 'This question is still open in Understanding. A conversation reply does not by itself confirm the record.'
          : 'This record is not confirmed yet. Only an owner decision confirms the agreed behavior.';
  return (
    <div className="field-discovery-resolution">
      <p>{recordMeaning}</p>
      {operation ? (
        <p className="mt-2" role={failed ? 'alert' : pending ? 'status' : undefined}>
          {pending
            ? operation.status === 'pending'
              ? `Your confirmation request is queued for ${owner}.`
              : `${owner} is reviewing this record. You do not need to send it again.`
            : failed
              ? `${owner} could not finish the reply to this request. Your record and message are saved.`
              : reply
                ? `${owner} replied in Conversations.`
                : 'The request completed, but no conversation reply is available in this view.'}
        </p>
      ) : null}
      {canRetry ? (
        <p className="text-fg-muted mt-2">
          Retry continues the saved request without adding another message or record version.
        </p>
      ) : null}
      {target || canRetry ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {canRetry ? (
            <Button
              size="sm"
              disabled={
                busy ||
                retrying ||
                !snapshot.enabled ||
                !snapshot.capabilities.chat ||
                snapshot.engagement.status !== 'active'
              }
              onClick={() => {
                if (sending.current || !operation) return;
                sending.current = true;
                setRetrying(true);
                setError('');
                void onAction({ type: 'operation.retry', operationId: operation.id })
                  .then((queued) => {
                    if (!queued)
                      setError('The request could not be retried. It is still saved; try again.');
                  })
                  .catch(() =>
                    setError('The request could not be retried. It is still saved; try again.'),
                  )
                  .finally(() => {
                    sending.current = false;
                    setRetrying(false);
                  });
              }}
            >
              {retrying ? 'Retrying owner reply…' : 'Retry owner reply'}
            </Button>
          ) : null}
          {target ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                onNavigate({ section: 'inbox', actorId: decision.owner, focusId: target.id })
              }
            >
              {reply ? `Read ${owner}’s reply` : 'View request in Conversations'}
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-danger mt-2">
          {error}
        </p>
      ) : null}
      {decision.resolution ? (
        <details className="field-discovery-reference">
          <summary>Recorded owner rationale · version {decision.resolution.version}</summary>
          <FieldChatMarkdown
            source={decision.resolution.rationale}
            snapshot={snapshot}
            onNavigate={onNavigate}
          />
          <FieldTime value={decision.resolution.at} />
        </details>
      ) : null}
    </div>
  );
}

export function FindingResponse({
  snapshot,
  busy,
  onAction,
  finding,
  onClose,
}: FieldPanelProps & {
  finding: FieldFinding;
  onClose: () => void;
}) {
  const id = useId();
  const [action, setAction] = useState<'dispute' | 'request_resolution' | 'request_deferral'>(
    'request_resolution',
  );
  const [response, setResponse] = useState('');
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  return (
    <form
      className="field-object-form space-y-4"
      aria-label="Respond to finding"
      onSubmit={(event) => {
        event.preventDefault();
        const parsed = fieldFindingRespondActionSchema.safeParse({
          type: 'finding.respond',
          findingId: finding.id,
          action,
          response,
          sourceIds,
        });
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? 'Check your response.');
          return;
        }
        setError('');
        void onAction(parsed.data).then((saved) => {
          if (saved) onClose();
        });
      }}
    >
      <div>
        <label htmlFor={`${id}-action`} className={FIELD_LABEL}>
          Response
        </label>
        <SelectField
          id={`${id}-action`}
          value={action}
          onValueChange={(value) => {
            if (
              value === 'dispute' ||
              value === 'request_resolution' ||
              value === 'request_deferral'
            )
              setAction(value);
          }}
          options={[
            { value: 'request_resolution', label: 'Request resolution review' },
            { value: 'dispute', label: 'Dispute the finding' },
            { value: 'request_deferral', label: 'Propose deferral' },
          ]}
        />
      </div>
      <div>
        <label htmlFor={`${id}-text`} className={FIELD_LABEL}>
          Reason and evidence
        </label>
        <textarea
          id={`${id}-text`}
          className={FIELD_INPUT}
          rows={4}
          maxLength={12000}
          required
          value={response}
          onChange={(event) => setResponse(event.target.value)}
        />
      </div>
      <FieldEvidencePicker snapshot={snapshot} value={sourceIds} onChange={setSourceIds} />
      <p className="text-fg-muted text-sm leading-6">
        The finding’s owner will review your response. Requesting resolution does not close the
        finding.
      </p>
      {error ? (
        <p role="alert" className="text-danger text-sm">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={busy || !snapshot.enabled || !response.trim()}>
          Send response
        </Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function DecisionBatchRequest({
  snapshot,
  decisions,
  pending,
  busy,
  onAction,
  onSent,
}: {
  snapshot: FieldSnapshot;
  decisions: FieldDecision[];
  pending: ReadonlySet<string>;
  busy: boolean;
  onAction: FieldPanelProps['onAction'];
  onSent: (count: number) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const open = decisions.filter(
    (decision) => decision.status === 'open' && !pending.has(`${decision.owner}:${decision.id}`),
  );
  if (open.length < 2) return null;
  const chosen = open.filter((decision) => selected.includes(decision.id));
  return (
    <details className="field-discovery-batch">
      <summary>Request several confirmations at once</summary>
      <p>
        Choose up to five open decisions. Each owner receives one request for their own decisions
        and records a separate disposition for each; this does not confirm them for you.
      </p>
      <fieldset
        disabled={
          busy ||
          !snapshot.enabled ||
          !snapshot.capabilities.chat ||
          snapshot.engagement.status !== 'active'
        }
      >
        <legend className="sr-only">Decisions to send for confirmation</legend>
        {open.map((decision) => (
          <label key={decision.id}>
            <input
              type="checkbox"
              checked={selected.includes(decision.id)}
              disabled={!selected.includes(decision.id) && chosen.length >= 5}
              onChange={(event) =>
                setSelected((previous) =>
                  event.target.checked
                    ? [...previous, decision.id]
                    : previous.filter((id) => id !== decision.id),
                )
              }
            />
            <span>
              {decision.title}
              <small>
                Version {decision.version} ·{' '}
                {snapshot.actors.find((actor) => actor.id === decision.owner)?.name ??
                  decision.owner}
              </small>
            </span>
          </label>
        ))}
      </fieldset>
      <Button
        size="sm"
        variant="secondary"
        disabled={busy || chosen.length === 0}
        onClick={() => {
          void onAction({
            type: 'decision.submit_batch',
            decisions: chosen.map((decision) => ({
              decisionId: decision.id,
              expectedVersion: decision.version,
            })),
          }).then((sent) => {
            if (sent) {
              setSelected([]);
              onSent(chosen.length);
            }
          });
        }}
      >
        Request {chosen.length || ''} {chosen.length === 1 ? 'confirmation' : 'confirmations'}
      </Button>
    </details>
  );
}

export function FieldDiscovery({
  snapshot,
  busy,
  onAction,
  onNavigate,
  onOpenEditor,
  focusId,
  focusRequest,
  viewRequest,
  onViewChange,
}: FieldPanelProps & {
  onNavigate: (destination: FieldDestination) => void;
  onOpenEditor: () => void;
  onViewChange?: () => void;
  focusId?: string | undefined;
  focusRequest?: number;
  viewRequest?:
    | {
        tab: 'brief' | 'decisions' | 'findings';
        request: number;
        showDecisionForm?: boolean;
      }
    | undefined;
}) {
  const [tab, setTab] = useState<'brief' | 'decisions' | 'findings'>('brief');
  const [editing, setEditing] = useState<string | null>(null);
  const [responding, setResponding] = useState<string | null>(null);
  const [ticketFinding, setTicketFinding] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [filter, setFilter] = useState('all');
  const panel = useRef<HTMLDivElement>(null);
  const discovery = snapshot.discovery;
  const focusedView = discovery?.decisions.some((item) => item.id === focusId)
    ? 'decisions'
    : discovery?.findings.some((item) => item.id === focusId)
      ? 'findings'
      : 'brief';
  useEffect(() => {
    if (!focusId) return;
    setTab(focusedView);
    setFilter('all');
    const frame = requestAnimationFrame(() => {
      const element = Array.from(
        panel.current?.querySelectorAll<HTMLElement>('[data-object-id]') ?? [],
      ).find((item) => item.dataset.objectId === focusId);
      element?.focus({ preventScroll: true });
      element?.scrollIntoView?.({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusId, focusRequest, focusedView]);
  useEffect(() => {
    if (!viewRequest) return;
    setTab(viewRequest.tab);
    setFilter('all');
    if (viewRequest.showDecisionForm) setEditing((previous) => previous ?? 'new');
    if (panel.current) panel.current.scrollTop = 0;
  }, [viewRequest]);
  if (!discovery) return null;
  const editDecision = discovery.decisions.find((item) => item.id === editing);
  const findingForTicket = discovery.findings.find((item) => item.id === ticketFinding);
  const openDecisions = discovery.decisions.filter(
    (item) => item.status === 'open' || item.status === 'proposed',
  );
  const openFindings = discovery.findings.filter(
    (item) => item.status !== 'resolved' && item.status !== 'deferred',
  );
  const pendingResponseIds = new Set(
    snapshot.operations
      .filter(
        (operation) =>
          operation.kind === 'turn' && ['pending', 'running'].includes(operation.status),
      )
      .map((operation) => operation.id),
  );
  const pendingOwnerReferences = new Set(
    snapshot.messages
      .filter(
        (message) =>
          message.role === 'learner' &&
          message.operationId &&
          pendingResponseIds.has(message.operationId),
      )
      .flatMap((message) => message.sourceIds.map((sourceId) => `${message.actorId}:${sourceId}`)),
  );
  return (
    <div ref={panel} className="field-object-panel field-discovery h-full overflow-y-auto">
      <FieldPanelHeading title="Brief & decisions">
        Build a shared understanding, agree the scope, and track what the evidence reveals.
      </FieldPanelHeading>
      <div className="field-discovery-tabs" aria-label="Engagement views">
        {(
          [
            { id: 'brief', label: 'Project brief' },
            {
              id: 'decisions',
              label: `Understanding${openDecisions.length ? ` · ${openDecisions.length} open` : ''}`,
            },
            {
              id: 'findings',
              label: `Findings${openFindings.length ? ` · ${openFindings.length} open` : ''}`,
            },
          ] as const
        ).map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={tab === item.id}
            onClick={() => {
              onViewChange?.();
              setTab(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      {feedback ? (
        <p role="status" className="field-object-feedback">
          {feedback}
        </p>
      ) : null}
      <section hidden={tab !== 'brief'} className="field-discovery-body" aria-label="Project brief">
        <article data-object-id={discovery.brief.id} tabIndex={-1}>
          <div className="field-object-meta">
            <span>Working brief · version {discovery.brief.version}</span>
            <span>{snapshot.actors.find((actor) => actor.id === discovery.brief.owner)?.name}</span>
          </div>
          <h3 className="field-discovery-title" data-field-tour="brief-overview">
            {discovery.brief.title}
          </h3>
          <p className="field-discovery-lead">{discovery.brief.summary}</p>
          <div className="field-discovery-prose">
            <FieldChatMarkdown source={discovery.brief.body} />
          </div>
          <div className="my-4 flex flex-wrap gap-2">
            <Button onClick={onOpenEditor}>Start building</Button>
            <Button
              variant="secondary"
              onClick={() =>
                onNavigate({
                  section: 'inbox',
                  actorId: 'jordan',
                  draft: stakeholderQuestions.jordan,
                  sourceIds: [discovery.brief.id],
                })
              }
            >
              Ask Jordan about the product
            </Button>
          </div>
          <p className="text-fg-muted mb-4 text-sm leading-6">
            You can prototype immediately or clarify a question first. Submit an early version to
            learn what works and what needs to change; you do not need a complete backlog.
          </p>
          <div className="field-discovery-columns">
            <section data-field-tour="brief-success">
              <h4>What success looks like</h4>
              <ul>
                {discovery.brief.successMeasures.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
            <section data-field-tour="brief-constraints">
              <h4>Known constraints</h4>
              <ul>
                {discovery.brief.constraints.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          </div>
        </article>
        <section className="field-discovery-section" aria-labelledby="field-outcomes-title">
          <h3 id="field-outcomes-title" data-field-tour="brief-outcomes">
            Delivery outcomes
          </h3>
          <p className="text-fg-muted text-sm leading-6">
            Use these to shape your first increment. You choose how to break down the work.
          </p>
          {discovery.outcomes.map((outcome) => (
            <details key={outcome.id} className="field-discovery-outcome">
              <summary>{outcome.title}</summary>
              <p>{outcome.description}</p>
              <ul>
                {outcome.acceptance.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </details>
          ))}
        </section>
        <section className="field-discovery-section" aria-labelledby="field-start-title">
          <h3 id="field-start-title">Start with what you know</h3>
          <ul className="field-discovery-list">
            {discovery.brief.startingPoints.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="secondary" onClick={onOpenEditor}>
              Explore the starter
            </Button>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setTab('decisions');
                setEditing('new');
              }}
            >
              Record understanding
            </Button>
            <Button variant="ghost" onClick={() => onNavigate({ section: 'board' })}>
              Plan an increment
            </Button>
          </div>
        </section>
        <section className="field-discovery-section" aria-labelledby="field-stakeholders-title">
          <h3 id="field-stakeholders-title">People who can help</h3>
          <p className="text-fg-muted text-sm leading-6">
            Choose the owner of the question in front of you. These are starting points, not a
            required interview checklist.
          </p>
          <ul className="field-discovery-directory">
            {snapshot.actors.map((actor) => (
              <li key={actor.id}>
                <div>
                  <strong>{actor.name}</strong>
                  <span>{actor.role}</span>
                  <p>{actor.focus}</p>
                  <button
                    className="text-link mt-2 text-left text-sm leading-6"
                    type="button"
                    onClick={() =>
                      onNavigate({
                        section: 'inbox',
                        actorId: actor.id,
                        draft: stakeholderQuestions[actor.id],
                        sourceIds: [discovery.brief.id],
                      })
                    }
                  >
                    {stakeholderQuestions[actor.id]}
                  </button>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onNavigate({ section: 'inbox', actorId: actor.id })}
                >
                  Talk to {actor.name.split(' ')[0]}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      </section>
      <section hidden={tab !== 'decisions'} aria-label="Current understanding">
        <div className="field-discovery-section-heading" data-field-tour="understanding">
          <div>
            <h3>Current understanding</h3>
            <p>Separate questions and assumptions from confirmed requirements.</p>
          </div>
          <Button
            size="sm"
            disabled={busy || !snapshot.enabled}
            aria-expanded={editing === 'new'}
            onClick={() => setEditing(editing === 'new' ? null : 'new')}
          >
            New record
          </Button>
        </div>
        {editing ? (
          <FieldDecisionForm
            key={editing}
            snapshot={snapshot}
            busy={busy}
            onAction={onAction}
            {...(editDecision ? { decision: editDecision } : {})}
            onClose={() => setEditing(null)}
          />
        ) : null}
        <DecisionBatchRequest
          snapshot={snapshot}
          decisions={openDecisions}
          pending={pendingOwnerReferences}
          busy={busy}
          onAction={onAction}
          onSent={(count) =>
            setFeedback(
              `Confirmation requested for ${count} decisions. Owner responses will appear in Conversations.`,
            )
          }
        />
        {discovery.decisions.length ? (
          <div className="field-discovery-records">
            {discovery.decisions.map((decision) => (
              <article
                key={decision.id}
                className="field-discovery-record"
                data-object-id={decision.id}
                data-focused={focusId === decision.id}
                tabIndex={-1}
              >
                <div className="field-discovery-record-heading" data-field-tour="decision-records">
                  <h4>{decision.title}</h4>
                  <FieldStatus
                    value={
                      decision.status === 'proposed'
                        ? 'awaiting_owner_confirmation'
                        : decision.status
                    }
                  />
                </div>
                <div className="field-object-meta">
                  <span>
                    {decision.kind === 'scope'
                      ? 'Scope decision'
                      : decision.kind === 'question'
                        ? 'Question'
                        : decision.kind === 'assumption'
                          ? 'Assumption'
                          : 'Requirement'}
                  </span>
                  <span>{decision.impact} impact</span>
                  <span>{snapshot.actors.find((actor) => actor.id === decision.owner)?.name}</span>
                  <span>Version {decision.version}</span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6">{decision.description}</p>
                {decision.acceptance.length ? (
                  <div className="mt-3">
                    <h5 className="text-sm font-medium">Acceptance examples</h5>
                    <ul className="field-discovery-list">
                      {decision.acceptance.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {decision.outcomeIds.length ? (
                  <p className="text-fg-muted mt-3 text-xs leading-5">
                    Supports{' '}
                    {decision.outcomeIds
                      .map((id) => discovery.outcomes.find((item) => item.id === id)?.title ?? id)
                      .join(' · ')}
                  </p>
                ) : null}
                {decision.sourceIds.length ? (
                  <details className="field-discovery-reference">
                    <summary>Supporting evidence ({decision.sourceIds.length})</summary>
                    <EvidenceList
                      snapshot={snapshot}
                      ids={decision.sourceIds}
                      revisions={decision.sourceRevisions}
                      onNavigate={onNavigate}
                    />
                  </details>
                ) : null}
                <DecisionConversation
                  snapshot={snapshot}
                  decision={decision}
                  busy={busy}
                  onAction={onAction}
                  onNavigate={onNavigate}
                />
                <div className="mt-4 flex flex-wrap gap-2">
                  {(decision.status === 'open' || decision.status === 'proposed') &&
                  !(
                    decision.confirmationOperationId &&
                    decisionOwnerConversation(snapshot, decision).operation?.status === 'failed'
                  ) ? (
                    <Button
                      size="sm"
                      disabled={
                        busy ||
                        !snapshot.enabled ||
                        !snapshot.capabilities.chat ||
                        snapshot.engagement.status !== 'active' ||
                        pendingOwnerReferences.has(`${decision.owner}:${decision.id}`)
                      }
                      onClick={() => {
                        void onAction({
                          type: 'decision.submit',
                          decisionId: decision.id,
                          expectedVersion: decision.version,
                        }).then((sent) => {
                          if (sent)
                            setFeedback(
                              `Confirmation requested from ${snapshot.actors.find((actor) => actor.id === decision.owner)?.name ?? decision.owner}. Their response will appear in Conversations.`,
                            );
                        });
                      }}
                    >
                      {decision.status === 'proposed'
                        ? 'Follow up with owner'
                        : 'Request confirmation'}
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      setEditing(decision.id);
                      panel.current?.scrollTo?.({ top: 0 });
                    }}
                  >
                    Edit record
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onNavigate({ section: 'inbox', actorId: decision.owner })}
                  >
                    Talk to owner
                  </Button>
                </div>
                {decision.history.length ? (
                  <details className="field-discovery-reference">
                    <summary>Record history ({decision.history.length} events)</summary>
                    <ol className="field-discovery-history">
                      {decisionHistoryGroups(decision.history).map(({ entry, count }, index) => (
                        <li key={`${entry.version}-${index}`}>
                          <strong>
                            Version {entry.version} ·{' '}
                            {entry.status === 'proposed' && entry.by === 'learner'
                              ? 'Confirmation requested'
                              : entry.status}
                          </strong>
                          {count > 1 ? (
                            <p>
                              Requested {count} times. These requests did not create a new version.
                            </p>
                          ) : null}
                          <p>{entry.description}</p>
                          <p>{entry.rationale}</p>
                          <span>
                            {entry.by === 'learner'
                              ? 'You'
                              : snapshot.actors.find((actor) => actor.id === entry.by)?.name}{' '}
                            · <FieldTime value={entry.at} />
                          </span>
                        </li>
                      ))}
                    </ol>
                  </details>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <FieldEmpty title="Build a shared understanding">
            Record what you learn from the brief, code, tests, and conversations. Questions can stay
            open while you investigate; the relevant owner confirms the agreed behavior.
          </FieldEmpty>
        )}
      </section>
      <section hidden={tab !== 'findings'} aria-label="Review findings">
        <div className="field-discovery-section-heading" data-field-tour="findings">
          <div>
            <h3>Review findings</h3>
            <p>Respond to the evidence, agree the follow-up, and verify the result.</p>
          </div>
          <div>
            <label htmlFor="finding-status" className={FIELD_LABEL}>
              Show
            </label>
            <SelectField
              id="finding-status"
              value={filter}
              onValueChange={setFilter}
              options={[
                { value: 'all', label: 'All findings' },
                { value: 'open', label: 'Needs attention' },
                { value: 'resolved', label: 'Resolved or deferred' },
              ]}
            />
          </div>
        </div>
        {findingForTicket ? (
          <FieldTicketForm
            key={findingForTicket.id}
            snapshot={snapshot}
            busy={busy}
            onAction={onAction}
            finding={findingForTicket}
            onClose={() => setTicketFinding(null)}
          />
        ) : null}
        {discovery.findings.length ? (
          <div className="field-discovery-records">
            {(filter === 'open' && !openFindings.length) ||
            (filter === 'resolved' && openFindings.length === discovery.findings.length) ? (
              <FieldEmpty title="No findings in this view">
                Choose All findings to review the full history.
              </FieldEmpty>
            ) : null}
            {discovery.findings
              .filter(
                (finding) =>
                  filter === 'all' ||
                  (filter === 'open'
                    ? !['resolved', 'deferred'].includes(finding.status)
                    : ['resolved', 'deferred'].includes(finding.status)),
              )
              .map((finding) => (
                <article
                  key={finding.id}
                  className="field-discovery-record"
                  data-object-id={finding.id}
                  data-focused={focusId === finding.id}
                  tabIndex={-1}
                >
                  <div className="field-discovery-record-heading" data-field-tour="finding-record">
                    <h4>{finding.title}</h4>
                    <FieldStatus value={finding.status} />
                  </div>
                  <div className="field-object-meta">
                    <span>{categories[finding.category].label}</span>
                    <span>{snapshot.actors.find((actor) => actor.id === finding.owner)?.name}</span>
                    {finding.revision ? <span>Revision {finding.revision.slice(0, 8)}</span> : null}
                  </div>
                  <p className="text-fg-muted mt-2 text-xs leading-5">
                    {categories[finding.category].meaning}
                  </p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6">
                    {finding.description}
                  </p>
                  <EvidenceList
                    snapshot={snapshot}
                    ids={[...new Set([...finding.decisionIds, ...finding.evidenceIds])]}
                    onNavigate={onNavigate}
                  />
                  {finding.response ? (
                    <div className="field-discovery-resolution">
                      <strong>Your response</strong>
                      <p>{finding.response}</p>
                    </div>
                  ) : null}
                  {finding.resolution ? (
                    <div className="field-discovery-resolution">
                      <strong>Latest recorded owner decision</strong>
                      <p>{finding.resolution.rationale}</p>
                      <EvidenceList
                        snapshot={snapshot}
                        ids={finding.resolution.evidenceIds}
                        onNavigate={onNavigate}
                      />
                    </div>
                  ) : null}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {finding.ticketId ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onNavigate({ section: 'board', focusId: finding.ticketId })}
                      >
                        View follow-up ticket
                      </Button>
                    ) : !['resolved', 'deferred'].includes(finding.status) ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => {
                          setTicketFinding(finding.id);
                          panel.current?.scrollTo?.({ top: 0 });
                        }}
                      >
                        Create follow-up ticket
                      </Button>
                    ) : null}
                    {finding.status !== 'resolved' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => setResponding(responding === finding.id ? null : finding.id)}
                        aria-expanded={responding === finding.id}
                      >
                        Respond to finding
                      </Button>
                    ) : null}
                  </div>
                  {responding === finding.id && finding.status !== 'resolved' ? (
                    <FindingResponse
                      snapshot={snapshot}
                      busy={busy}
                      onAction={onAction}
                      finding={finding}
                      onClose={() => setResponding(null)}
                    />
                  ) : null}
                </article>
              ))}
          </div>
        ) : (
          <FieldEmpty title="Findings follow the evidence">
            Share a working increment with the relevant colleague. Reviews can identify defects,
            clarify requirements, propose scope changes, or recognize a failure you prevented.
          </FieldEmpty>
        )}
      </section>
    </div>
  );
}
